import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { ROLE_LOAN_OFFICER, canAccessNavItem } from "../accessControl";
import { callApi, getInlineLoader } from "../Helpers";
import { MdHandshake, MdCalendarToday } from "react-icons/md";

export default function View({ uuid }) {

    const appContext = useContext(AppContext);

    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);

    const [customerUuid, setCustomerUuid] = useState('');
    
    // Resale specific state
    const [resaleMotorcycles, setResaleMotorcycles] = useState([]);
    const [resaleUuid, setResaleUuid] = useState('');
    const [motorcycleUuid, setMotorcycleUuid] = useState(''); 

    const [saleType, setSaleType] = useState('');
    const [repaymentType, setRepaymentType] = useState('weekly');
    const [assignedTo, setAssignedTo] = useState('');
    const [dueDate, setDueDate] = useState('');
    
    const [contractDate, setContractDate] = useState(new Date().toISOString().split('T')[0]);
    
    const [regNumber, setRegNumber] = useState('');
    const [gpsTrackerNumber, setGpsTrackerNumber] = useState('');
    const [durationMonths, setDurationMonths] = useState('16'); 

    const [data, setData] = useState(null);

    async function fetchResaleMotorcycles() {
        await callApi('get-resale-motorcycles', {}).then(res => {
            if (res.status === 1) {
                // DIAGNOSTIC LOG 1: What is the shape of the stock array?
                console.log("--- LOG 1: RAW RESALE STOCK FROM API ---");
                console.log(res.data);
                setResaleMotorcycles(res.data || []);
            }
        });
    }

    async function init() {
        setReady(false);
        if (!canAccessNavItem(appContext.currentUserData, "sales")) {
            appContext.tellError("You do not have access to sales.");
            appContext.navTo({ item: "dashboard" });
            return;
        }

        await Promise.all([
            appContext.getAllCustomers(),
            appContext.getAllAdmins(),
            fetchResaleMotorcycles(),
            (async () => {
                if (uuid) await getData();
            })(),
        ]);
        setReady(true);
    }

    async function getData() {
        await callApi('get-resale-motorcycles', { uuid }).then((response) => {
            if (response.status === 1) {
                setData(response.data);
                setCustomerUuid(response.data.customerID || '');
                setResaleUuid(response.data.resaleUuid || '');
                setMotorcycleUuid(response.data.motorcycleUuiD || '');
                setSaleType(response.data.saleType || '');
                setRepaymentType(response.data.repaymentType || '');
                setDueDate(response.data.dueDate || '');
                setRegNumber(response.data.regNumber || '');
                setGpsTrackerNumber(response.data.gpsTrackerNumber || '');
                setDurationMonths(response.data.durationMonths ? String(response.data.durationMonths) : '16');
                setAssignedTo(response.data.assignedTo || '');
                
                if (response.data.contractDate) {
                    setContractDate(response.data.contractDate);
                }
            } else {
                appContext.tellError('Could not fetch data');
            }
        });
    }

    // 1. UPDATE THIS FUNCTION
    const handleResaleSelection = (e) => {
        const selectedId = e.target.value;
        
        console.log("Target value selected:", selectedId);
        setResaleUuid(selectedId);

        if (!selectedId) {
            setMotorcycleUuid('');
            setRegNumber('');
            setGpsTrackerNumber('');
            return;
        }

        // Expanded the search to include resaleUuid and _id
        const selectedBike = resaleMotorcycles.find(item => 
            String(item.resaleUuid) === String(selectedId) || 
            String(item.uuid) === String(selectedId) || 
            String(item.id) === String(selectedId) ||
            String(item._id) === String(selectedId)
        );

        console.log("Result of .find():", selectedBike);

        if (selectedBike) {
            setMotorcycleUuid(selectedBike.motorcycleID || selectedBike.motorcycleUuid || selectedBike.motorcycle_id || '');
            setRegNumber(selectedBike.regNumber || selectedBike.registrationNumber || '');
            setGpsTrackerNumber(selectedBike.gpsTrackerNumber || selectedBike.gpsTrackerID || selectedBike.gps_tracker || '');
        }
    };

    async function create() {
        if (!customerUuid) return appContext.tellError("Customer field is compulsory");
        if (!motorcycleUuid || !resaleUuid) return appContext.tellError("Motorcycle selection is compulsory");
        if (!saleType) return appContext.tellError("Sale Type classification is compulsory");
        if (!contractDate) return appContext.tellError("Contract Date is compulsory");

        if (saleType !== 'cash') {
            if (!regNumber || !regNumber.trim()) return appContext.tellError("Registration Number is compulsory");
            if (!gpsTrackerNumber || !gpsTrackerNumber.trim()) return appContext.tellError("GPS Tracker ID is compulsory");
            if (!assignedTo) return appContext.tellError("Loan Officer not Selected");
        }

        if (saleType === 'loan') {
            if (!repaymentType) return appContext.tellError("Repayment Contract is compulsory");
            if (!durationMonths || parseInt(durationMonths) <= 0) return appContext.tellError("Contract Duration is compulsory");
        }

        setLoading(true);
        await callApi('create-sale', {
            customerUuid,
            motorcycleUuid,
            resaleUuid, 
            saleType,
            repaymentType,
            assignedTo,
            contractDate, 
            regNumber: saleType === 'cash' ? null : (regNumber || '').trim().toUpperCase(),
            gpsTrackerNumber: saleType === 'cash' ? null : (gpsTrackerNumber || '').trim(),
            durationMonths: saleType === 'loan' ? parseInt(durationMonths) : null
        }).then(response => {
            if (response.status === 1) {
                appContext.tellMessage('Successful');
                appContext.navBack();
                if(appContext.getAllSales) appContext.getAllSales(); 
            } else {
                appContext.tellError(response.msg);
            }
        });
        setLoading(false);
    }

    async function update() {
        if (!customerUuid) return appContext.tellError("Customer field is compulsory");
        if (!motorcycleUuid || !resaleUuid) return appContext.tellError("Motorcycle selection is compulsory");
        if (!saleType) return appContext.tellError("Sale Type classification is compulsory");
        if (!contractDate) return appContext.tellError("Contract Date is compulsory");
        
        if (saleType !== 'cash') {
            if (!regNumber || !regNumber.trim()) return appContext.tellError("Registration Number is compulsory");
            if (!gpsTrackerNumber || !gpsTrackerNumber.trim()) return appContext.tellError("GPS Tracker ID is compulsory");
        }

        if (saleType === 'loan') {
            if (!repaymentType) return appContext.tellError("Repayment Contract is compulsory");
            if (!durationMonths || parseInt(durationMonths) <= 0) return appContext.tellError("Contract Duration is compulsory");
        }

        setLoading(true);
        await callApi('update-sale', {
            uuid,
            customerUuid,
            motorcycleUuid,
            resaleUuid,
            saleType,
            repaymentType,
            dueDate,
            assignedTo,
            contractDate, 
            regNumber: saleType === 'cash' ? null : (regNumber || '').trim().toUpperCase(),
            gpsTrackerNumber: saleType === 'cash' ? null : (gpsTrackerNumber || '').trim(),
            durationMonths: saleType === 'loan' ? parseInt(durationMonths) : null
        }).then(response => {
            if (response.status === 1) {
                appContext.tellMessage('Successful');
                appContext.navBack();
                if(appContext.getAllSales) appContext.getAllSales();
            } else {
                appContext.tellError(response.msg);
            }
        });
        setLoading(false);
    }

    useEffect(() => {
        init();
    }, [uuid]);

    useEffect(() => {
        if (saleType === 'cash') {
            setRepaymentType('');
            setDueDate('');
            setDurationMonths('');
        } else if (saleType === 'loan') {
            setRepaymentType('weekly'); 
            setDurationMonths('16');
        }
    }, [saleType]);

    useEffect(() => {
        appContext.setShowOverlayLoader(loading);
        return () => appContext.setShowOverlayLoader(false);
    }, [loading]);

    if (!ready) {
        return (
            <div className="container mSupportLoading">
                {getInlineLoader()}
            </div>
        );
    }

    return (
        <div className="container">
            <div className="app-form-page">
                <div className="app-form-shell">
                    <div className="app-form-header">
                        <div>
                            <h6 className="app-form-title">{uuid ? "Edit Resale Record" : "Add Resale Transaction"}</h6>
                        </div>
                        <div className="app-form-badge">
                            <MdHandshake size={18} />
                            {uuid ? "Updating contract" : "Creating contract"}
                        </div>
                    </div>

                    <div className="app-form-section">
                        <div className="app-form-summary-grid">
                            <div className="app-form-summary-card">
                                <span>Customers</span>
                                <strong>{appContext.allCustomers?.length || 0} available</strong>
                            </div>
                            <div className="app-form-summary-card">
                                <span>Resale Stock</span>
                                <strong>{resaleMotorcycles.length} units available</strong>
                            </div>
                            <div className="app-form-summary-card">
                                <span>Sale Mode</span>
                                <strong>{saleType ? saleType.toUpperCase() : "Unselected"}</strong>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label">Customer *</label>
                                    <select value={customerUuid || ''} onChange={(e) => setCustomerUuid(e.target.value)} className="form-control" required>
                                        <option value="">--Select Customer--</option>
                                        {appContext.allCustomers?.map((item, i) => (
                                            <option key={i} value={item.uuid || item.id}>{item.fullname}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* 2. UPDATE THIS DROPDOWN */}
                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label">Returned Motorcycle Unit *</label>
                                    <select value={resaleUuid || ''} onChange={handleResaleSelection} className="form-control" required>
                                        <option value="">--Select Specific Motorcycle--</option>
                                        {resaleMotorcycles.map((item, i) => {
                                            // Ensure we grab the actual primary key, whatever the backend named it
                                            const primaryKey = item.resaleUuid || item.uuid || item.id || item._id;
                                            
                                            return (
                                                <option key={i} value={primaryKey}>
                                                    {item.model || item.motorcycleModel || 'Motorcycle'} - Reg: {item.regNumber || 'N/A'} 
                                                </option>
                                            )
                                        })}
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label">Sale Configuration Type *</label>
                                    <select value={saleType || ''} onChange={(e) => setSaleType(e.target.value)} className="form-control" required>
                                        <option value="">--Select Sale Type--</option>
                                        <option value="cash">Cash Payment</option>
                                        <option value="loan">Financing Hire-Purchase (Loan)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label d-flex align-items-center gap-2">
                                        <MdCalendarToday size={16} /> Contract / Sale Date *
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={contractDate || ''}
                                        max={new Date().toISOString().split('T')[0]} 
                                        onChange={(e) => setContractDate(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {saleType !== 'cash' && (
                                <>
                                    <div className="col-md-6 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">Registration Number *</label>
                                            <input 
                                                className="form-control"
                                                value={regNumber || ''} 
                                                onChange={(e) => setRegNumber(e.target.value)}
                                                placeholder="e.g. MC 542 DFR"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">GPS Tracker Device ID *</label>
                                            <input 
                                                className="form-control"
                                                value={gpsTrackerNumber || ''} 
                                                onChange={(e) => setGpsTrackerNumber(e.target.value)}
                                                placeholder="Enter tracking system serial ID"
                                                required
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {saleType === 'loan' && (
                                <>
                                    <div className="col-md-3 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">Duration (Months) *</label>
                                            <input 
                                                className="form-control"
                                                type="number"
                                                min="1"
                                                value={durationMonths || ''}
                                                onChange={(e) => setDurationMonths(e.target.value)}
                                                placeholder="e.g. 16"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-3 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">Assign Loan Officer</label>
                                            
                                            <select 
                                                value={assignedTo || ''} 
                                                onChange={(e) => setAssignedTo(e.target.value)} 
                                                className="form-control" 
                                                required
                                            >
                                                <option value="">--Select Officer--</option>
                                                {appContext.allAdmins
                                                    ?.filter(admin => admin.role === ROLE_LOAN_OFFICER || admin.role === 'loan_officer')
                                                    ?.map((item, i) => (
                                                        <option key={i} value={item.id}>
                                                            {item.fullname}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="col-12 mt-3 mb-3 text-end app-form-submit-row">
                                <button
                                    className="btn btn-primary btn-block app-form-submit-btn"
                                    onClick={uuid ? update : create}
                                >
                                    {uuid ? "UPDATE RESALE CONTRACT" : "REGISTER RESALE TRANSACTION"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}